import React from 'react';
import { LightbulbIcon, TargetIcon, FlameIcon, TrophyIcon, BrainIcon } from 'lucide-react';
import type { DjayEmotion } from './DjayAvatar';
import type { BubbleColorScheme } from './DjayBubble';
import type { LearnerContext } from './djayMessages';
/**
 * Contextual Menu Builder — Djay's "What shall we do?" panel.
 *
 * Extracted from FloatingDjay.tsx (audit P1, ronde 2 2026) so the
 * 1300-line floating component focuses purely on interaction state, not
 * the static catalog of menu items.
 *
 * Pure function — receives current view + learner context + handler
 * callbacks, returns the items array. No React state, no hooks.
 *
 * Ronde 7: extracted `makeOefenenItem` factory so the four context-aware
 * "Ga oefenen" buttons share one source of truth for styling/label/icon.
 */
export type ItemAccessory = 'book' | 'sparkles' | 'none';
export interface PanelMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  bgClass: string;
  hoverClass: string;
  textClass: string;
  onClick: () => void;
  /** Hint Djay speaks when this item is hovered/focused — first-person, friendly */
  bubbleHint: string;
  /** Color scheme for the speech bubble when this item is hovered */
  bubbleColor: BubbleColorScheme;
  /** Emotion Djay shows when this item is hovered/focused */
  hoverEmotion: DjayEmotion;
  /** Optional accessory prop that floats next to Djay when this item is focused */
  accessory?: ItemAccessory;
}
export interface MenuHandlers {
  onTipClick: () => void;
  onNavigate: (path: string) => void;
  onErrorTipClick: (tip: string) => void;
}
export interface MenuErrorPattern {
  recommendation: string;
  familyName: string;
}
/**
 * Factory for the "Ga oefenen" menu item — used across home/progress/
 * collection/default views. Only the bubble hint differs per context.
 */
function makeOefenenItem(bubbleHint: string, onNavigate: (path: string) => void): PanelMenuItem {
  return {
    id: 'oefenen',
    label: 'Ga oefenen',
    icon: <TargetIcon className="w-4 h-4" data-id="element-3732" />,
    bgClass: 'bg-orange-50',
    hoverClass: 'hover:bg-orange-100',
    textClass: 'text-orange-700',
    onClick: () => onNavigate('learningLab'),
    bubbleHint,
    bubbleColor: 'default',
    hoverEmotion: 'excited',
    accessory: 'sparkles'
  };
}
export function getContextualMenuItems(currentView: string, learnerContext: LearnerContext, errorPatterns: MenuErrorPattern[], handlers: MenuHandlers): PanelMenuItem[] {
  const {
    streak,
    displayName
  } = learnerContext;
  const items: PanelMenuItem[] = [];
  // If there are error patterns, show a personalized learning tip
  if (errorPatterns.length > 0) {
    const topPattern = errorPatterns[0];
    items.push({
      id: 'error_tip',
      label: '💡 Tip voor jou',
      icon: <LightbulbIcon className="w-4 h-4" data-id="element-3733" />,
      bgClass: 'bg-violet-50',
      hoverClass: 'hover:bg-violet-100',
      textClass: 'text-violet-700',
      onClick: () => handlers.onErrorTipClick(topPattern.recommendation),
      bubbleHint: `Ik zag iets bij ${topPattern.familyName.toLowerCase()} — wil je m'n tip horen? ✨`,
      bubbleColor: 'violet',
      hoverEmotion: 'encouraging',
      accessory: 'sparkles'
    });
  }
  // Always show "Weetje van de dag"
  items.push({
    id: 'weetje',
    label: 'Weetje van de dag',
    icon: <LightbulbIcon className="w-4 h-4" data-id="element-3734" />,
    bgClass: 'bg-amber-50',
    hoverClass: 'hover:bg-amber-100',
    textClass: 'text-amber-700',
    onClick: handlers.onTipClick,
    bubbleHint: 'Ik heb elke dag een nieuw weetje voor je! Klik maar 🤓',
    bubbleColor: 'amber',
    hoverEmotion: 'pointing',
    accessory: 'book'
  });
  switch (currentView) {
    case 'home':
      items.push(makeOefenenItem(`Klaar om te trainen, ${displayName}? Ik heb een plan! 💪`, handlers.onNavigate));
      break;
    case 'subjects':
    case 'modules':
      items.push({
        id: 'voortgang',
        label: 'Bekijk voortgang',
        icon: <BrainIcon className="w-4 h-4" data-id="element-3735" />,
        bgClass: 'bg-emerald-50',
        hoverClass: 'hover:bg-emerald-100',
        textClass: 'text-emerald-700',
        onClick: () => handlers.onNavigate('progress'),
        bubbleHint: 'Kijk hoe ver je al gekomen bent! Ik ben trots! 🌟',
        bubbleColor: 'emerald',
        hoverEmotion: 'proud',
        accessory: 'sparkles'
      });
      break;
    case 'progress':
      items.push(makeOefenenItem('Tijd om die voortgang te laten groeien! 🚀', handlers.onNavigate));
      break;
    case 'collection':
      items.push(makeOefenenItem('Verdien meer trofeeën — ga oefenen! 🏆', handlers.onNavigate));
      break;
    case 'learningLab':
    case 'lab':
      items.push({
        id: 'collectie',
        label: 'Mijn collectie',
        icon: <TrophyIcon className="w-4 h-4" data-id="element-3736" />,
        bgClass: 'bg-violet-50',
        hoverClass: 'hover:bg-violet-100',
        textClass: 'text-violet-700',
        onClick: () => handlers.onNavigate('collection'),
        bubbleHint: 'Bekijk alle trofeeën die je verdiend hebt! ✨',
        bubbleColor: 'violet',
        hoverEmotion: 'proud',
        accessory: 'sparkles'
      });
      break;
    default:
      items.push(makeOefenenItem('Kom, we gaan oefenen! 💪', handlers.onNavigate));
      break;
  }
  if (streak > 0) {
    items.push({
      id: 'streak',
      label: `Mijn streak: ${streak} dagen`,
      icon: <FlameIcon className="w-4 h-4 text-orange-500" data-id="element-3737" />,
      bgClass: 'bg-rose-50',
      hoverClass: 'hover:bg-rose-100',
      textClass: 'text-rose-700',
      onClick: () => handlers.onNavigate('progress'),
      bubbleHint: streak >= 7 ? `${streak} dagen op rij?! Je bent een legende! 🔥👑` : `${streak} dagen streak — ga zo door! 🔥`,
      bubbleColor: 'amber',
      hoverEmotion: streak >= 7 ? 'celebrating' : 'excited',
      accessory: 'sparkles'
    });
  }
  return items;
}